import React from 'react';
import PropTypes from 'prop-types';
import { get, orderBy, find } from 'lodash';
import withData from '@bd/bdcore-data';
import { buildThumbnailUrl } from '@bd/bdcore-utilities';
import withStyles, { css } from '@bd/bdui-styles';
import LightBox from '@bd/bdui-lightbox';
import { userLogout } from '@bd/bdcore-data/build/User/actions';
import HomeCardAlt from '../../components/HomeCard';
import PlanCard from '../../components/PlanCard';
import CommunityCard from '../../components/CommunityCard';
import FavoriteToggle from './FavoriteToggle';
import { FavoriteEdit, FavoriteUpload } from '../../components/Favorites';
import ToggleElement from '../ToggleElement';
import menuToggler from '../menuToggler';

const defaultStyles = css`
  .NoBorder {
    border: 0;
  }
  .PhotoCard_wrapper {
    width: 100%;
    max-width: 300px;
    margin: 2px;
    transition: all .3s ease-out;
    @media(min-width: 992px) {
      max-width: 450px;
    }
    &:hover {
      background-color: #f1f1f1;
      @media(min-width: 992px) {
        box-shadow: rgba(0, 0, 0, 0.2) 0px 15px 20px 0px;
        transform: scale(1.005);
      }
    }
  }
`;

const videoBoxStyle = css`
  display: flex;
  flex-flow: column wrap;
  justify-content: center;
  text-align: center;
  width: 80%;
  padding-top: 45%;
  position: relative;
  margin: 0 auto;
  align-items: center;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    right:0;
    bottom:0;
    width:100%;
    height:100%;
  }
`;

const categories = {
  home: {
    title: 'Homes',
    render: (favorite, { homes }) => {
      const home = homes.find(h => h._id === favorite.favoriteId);

      if (!home) {
        return null;
      }

      return (
        <HomeCardAlt home={home} favorite={favorite} fullWidth key={home._id} className="m-1 NoBorder" />
      );
    },
  },
  plan: {
    title: 'Floor Plans',
    render: (favorite, { plans }) => {
      const plan = plans.find(p => p._id === favorite.favoriteId);

      if (!plan) {
        return null;
      }

      return (
        <PlanCard plan={plan} favorite={favorite} fullWidth key={plan._id} />
      );
    },
  },
  community: {
    title: 'Communities',
    render: (favorite, { communities }) => {
      const community = communities.find(c => c._id === favorite.favoriteId);

      if (!community) {
        return null;
      }

      return (
        <div key={community._id} className="col-12 col-sm-6 col-xl-4">
          <CommunityCard community={community} favorite={favorite} fullWidth />
        </div>
      );
    },
  },
  content: {
    title: 'Other Items',
  },
  upload: {
    title: 'Uploads',
    alwaysShow: true,
    allow: ({ user }) => !!user,
  },
};

function getActiveTypes(favorites, props) {
  return Object.keys(categories)
    .map(type => ({ type, items: favorites.filter(f => f.type === type).length }))
    .filter(({ items, type }) =>
      (!categories[type].allow || categories[type].allow(props))
      && (items || categories[type].alwaysShow),
    );
}

class FavoritesCategories extends React.Component {
  constructor(props) {
    super(props);

    const activeTypes = getActiveTypes(props.favorites, props);

    this.state = {
      activeTypes,
      selectedType: get(activeTypes, '[0].type'),
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.favorites.length !== this.props.favorites.length) {
      this.setTypes(this.props);
    }
  }

  setTypes(props) {
    const activeTypes = getActiveTypes(props.favorites, props);
    const existingType = get(find(activeTypes, { type: this.state.selectedType }), 'type');

    this.setState({
      activeTypes,
      selectedType: existingType || get(activeTypes, '[0].type'),
    });
  }

  selectType = type => {
    this.setState({
      selectedType: type,
    });
  }

  render() {
    const {
      activateMenu,
      isActiveMenu,
      favorites,
      className,
      user,
      dispatch,
    } = this.props;
    const { selectedType, activeTypes } = this.state;
    const filteredFavorites = favorites.filter(f => f.type === selectedType);

    return (
      <div className={className}>
        <div>
          <span role="button" tabIndex="0" className={`PageMenu_title ${isActiveMenu('favorite') ? 'PageMenu_titleAlt' : ''}`} onClick={() => activateMenu('favorite')}>Favorites</span>
          <ul className={`PageMenu_list ${isActiveMenu('favorite') ? 'open' : ''}`}>
            {
              activeTypes
                .map(({ type, items }) => (
                  <li className="PageMenu_listItem" key={type}>
                    <span className={`${selectedType === type ? ' active' : ''}`} onClick={() => { this.selectType(type); activateMenu('favorite'); }} role="button" tabIndex={0}>
                      <strong>{`${categories[type].title}`}</strong> {`${items}`}
                    </span>
                  </li>
                ))
            }
          </ul>
          <div className="FavoritesActions">
            {
              user &&
              <div className="ClientLogin_logout">
                <button type="button" className="btn" onClick={() => dispatch(userLogout())}>Logout</button>
              </div>
            }
            {
              selectedType === 'upload' &&
              user &&
              <ToggleElement className="FavoritesUpload" button={<button type="button" className="btn ml-3">Uploads</button>}>
                <FavoriteUpload />
              </ToggleElement>
            }
          </div>
          <div className="container-fluid px-0 pb-5">
            {
              selectedType &&
              !['content', 'upload'].includes(selectedType) &&
              <div className={`Cards_Wrap Cards_Wrap-${selectedType}`}>
                {
                  orderBy(filteredFavorites, 'favoritedAt', 'desc')
                    .map(favorite => categories[selectedType].render(favorite, this.props))
                }
              </div>
            }
            {
              ['content', 'upload'].includes(selectedType) &&
              <LightBox
                items={orderBy(filteredFavorites, 'favoritedAt', 'desc').map(favorite => {
                  if (favorite.iframe) {
                    return {
                      element: (
                        <div className={videoBoxStyle}>
                          <iframe src={favorite.favoriteId} title={favorite.favoriteId} />
                        </div>
                      ),
                    };
                  }
                  return favorite;
                })}
                onlyWithIndex
              >
                <ul className="VideoGallery_List list-unstyled row m-0 justify-content-center">
                  {
                    orderBy(filteredFavorites, 'favoritedAt', 'desc').map((favorite, index) => (
                      <li className="PhotoCard_wrapper" key={favorite._id}>
                        <div className="FavoriteCardWrap" data-index={index}>
                          <div className="PhotoCard" style={{ backgroundImage: `url(${buildThumbnailUrl(get(favorite, 'image.contentUrl', get(favorite, 'video_thumbnail.contentUrl', get(favorite, 'image'))), { width: 800, height: 450, exact: true })})` }} />
                          <div className="Favorite_circle">
                            <FavoriteToggle
                              favorite={favorite}
                            />
                          </div>
                          {
                            favorite.iframe &&
                            <div className="PlayIcon" >
                              <div className="PlayIcon_triangle" />
                            </div>
                          }
                        </div>
                        <div className="FavoriteEditWrap">
                          <FavoriteEdit favorite={favorite} />
                        </div>
                      </li>
                    ))
                  }
                </ul>
              </LightBox>
            }
          </div>
        </div>
      </div>
    );
  }
}

FavoritesCategories.propTypes = {
  activateMenu: PropTypes.func.isRequired,
  isActiveMenu: PropTypes.func.isRequired,
  className: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  favorites: PropTypes.arrayOf(PropTypes.object),
  user: PropTypes.object,
};

FavoritesCategories.defaultProps = {
  className: '',
  favorites: [],
  user: undefined,
};

export default withData(
  {
    favorites: true,
    homes: true,
    communities: true,
    plans: true,
    lots: true,
  },
  {
    required: {},
  },
)(withStyles(defaultStyles)(menuToggler(FavoritesCategories)));



// WEBPACK FOOTER //
// ./src/bdcoreCandidates/Favorites/FavoritesCategories.js